import { useState, useEffect } from 'react';
import {
    ArrowRight, CheckCircle, Shield, User, MapPin,
    Lock, Building, Phone, Mail, Loader2, Play, ChevronLeft,
    Check, AlertCircle, Briefcase, Map
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { userInvitationService } from '../utils/users/userInvitationService';
import { UserInvitation, UserOnboardingData } from '../types/userInvitation';

interface UserOnboardingFormProps {
    token: string;
}

export default function UserOnboardingForm({ token }: UserOnboardingFormProps) {
    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [invitation, setInvitation] = useState<UserInvitation | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Form Data
    const [formData, setFormData] = useState<UserOnboardingData>({
        name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        address: {
            street: '',
            city: '',
            state: '',
            zip: '',
        },
    });

    // Load Invitation
    useEffect(() => {
        const loadInvitation = async () => {
            try {
                if (!token) throw new Error('No token provided');
                const invite = await userInvitationService.getInvitationByToken(token);

                if (!invite) throw new Error('Invalid or expired invitation');
                if (invite.status === 'completed') throw new Error('Invitation already used');
                if (new Date() > new Date(invite.expiresAt)) throw new Error('Invitation expired');

                setInvitation(invite);
                setFormData(prev => ({
                    ...prev,
                    name: invite.name || '',
                    email: invite.email || '',
                    phone: invite.phoneNumber || '',
                }));
            } catch (err: any) {
                setError(err.message || 'Failed to load invitation');
            } finally {
                setIsLoading(false);
            }
        };
        loadInvitation();
    }, [token]);

    const handleChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleAddressChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            address: { ...prev.address, [field]: value }
        }));
    };

    const validateStep = (currentStep: number): boolean => {
        if (currentStep === 1) {
            if (!formData.name) { toast.error('Name is required'); return false; }
            if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { toast.error('Valid email required'); return false; }
            return true;
        }
        if (currentStep === 2) {
            if (!formData.address.street || !formData.address.city || !formData.address.state || !formData.address.zip) {
                toast.error('All address fields are required');
                return false;
            }
            return true;
        }
        if (currentStep === 3) {
            if (!formData.password || formData.password.length < 8) { toast.error('Password must be at least 8 characters'); return false; }
            if (formData.password !== formData.confirmPassword) { toast.error('Passwords do not match'); return false; }
            return true;
        }
        return true;
    };

    const handleNext = () => {
        if (validateStep(step)) setStep(prev => prev + 1);
    };

    const handleSubmit = async () => {
        if (!validateStep(3)) return;
        setIsSubmitting(true);
        try {
            const result = await userInvitationService.completeOnboarding(token, formData);
            if (result.success) {
                toast.success('Account created successfully!');
                // Redirect to login or auto-login (for now reload to trigger auth logic)
                window.location.href = '/';
            } else {
                toast.error(result.message);
            }
        } catch (err: any) {
            toast.error(err.message || 'Failed to create account');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-gray-50">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
        );
    }

    if (error || !invitation) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-gray-50 p-4">
                <div className="max-w-md w-full p-8 bg-white border border-gray-200 rounded-2xl shadow-xl text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertCircle className="w-8 h-8 text-red-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
                    <p className="text-gray-500 mb-6">{error || 'Invalid invitation link'}</p>
                    <Button onClick={() => window.location.href = '/'} className="w-full">
                        Return to Home
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen w-full bg-gray-50 font-sans text-slate-900">
            {/* Left Panel - Hero/Info (Hidden on mobile) */}
            <div className="hidden lg:flex w-1/2 xl:w-5/12 bg-slate-900 relative overflow-hidden flex-col justify-between p-12 text-white">
                {/* Background effects */}
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>

                {/* Brand */}
                <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-10">
                        <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                            <Shield className="w-6 h-6 text-white" />
                        </div>
                        <span className="font-bold text-2xl tracking-tight">OilWellX</span>
                    </div>

                    <div className="space-y-6 max-w-lg">
                        <h1 className="text-5xl font-extrabold leading-tight">
                            Welcome to <span className="text-blue-400">{invitation.companyName}</span>
                        </h1>
                        <p className="text-lg text-slate-300 leading-relaxed">
                            You've been invited by <strong className="text-white">{invitation.invitedByName}</strong> to join the team.
                            Complete your setup to access the platform.
                        </p>
                    </div>
                </div>

                {/* Footer Features */}
                <div className="relative z-10 grid grid-cols-2 gap-6">
                    <div className="p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
                        <User className="w-6 h-6 text-blue-400 mb-3" />
                        <div className="font-semibold text-lg">{invitation.role === 'manager' ? 'Manager Access' : 'Pro Access'}</div>
                        <div className="text-sm text-slate-400">{invitation.role === 'manager' ? 'Full administrative control' : 'Field and operations tools'}</div>
                    </div>
                    <div className="p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
                        <Shield className="w-6 h-6 text-green-400 mb-3" />
                        <div className="font-semibold text-lg">Secure Setup</div>
                        <div className="text-sm text-slate-400">Enterprise-grade security</div>
                    </div>
                </div>
            </div>

            {/* Right Panel - Form */}
            <div className="flex-1 flex flex-col justify-center items-center p-6 lg:p-12 relative">
                <div className="w-full max-w-lg">

                    {/* Progress Header */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-4">
                            <span className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Step {step} of 3</span>
                            <span className="text-sm font-medium text-blue-600">{step === 1 ? 'Personal Info' : step === 2 ? 'Address' : 'Security'}</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-blue-600 transition-all duration-500 ease-out"
                                style={{ width: step === 1 ? '33%' : step === 2 ? '66%' : '100%' }}
                            />
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-gray-100 overflow-hidden">
                        <div className="p-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">
                                {step === 1 && 'Confirm Your Details'}
                                {step === 2 && 'Where are you based?'}
                                {step === 3 && 'Secure Your Account'}
                            </h2>

                            {/* Step 1: Identity */}
                            {step === 1 && (
                                <div className="space-y-5 animate-in slide-in-from-right-4 fade-in duration-300">
                                    <div className="space-y-1.5">
                                        <Label className="text-gray-700">Full Name</Label>
                                        <div className="relative">
                                            <User className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                                            <Input
                                                value={formData.name}
                                                onChange={(e) => handleChange('name', e.target.value)}
                                                placeholder="John Doe"
                                                className="pl-10 h-11 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                                                autoFocus
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label className="text-gray-700">Email Address</Label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                                            <Input
                                                value={formData.email}
                                                onChange={(e) => handleChange('email', e.target.value)}
                                                placeholder="you@company.com"
                                                className="pl-10 h-11 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label className="text-gray-700">Phone Number</Label>
                                        <div className="relative">
                                            <Phone className="absolute left-3 top-3 w-5 h-5 text-green-600" />
                                            <Input
                                                value={formData.phone}
                                                disabled
                                                className="pl-10 h-11 bg-green-50 border-green-200 text-green-800 font-medium cursor-not-allowed"
                                            />
                                            <div className="absolute right-3 top-3 flex items-center gap-1 text-xs font-medium text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
                                                <CheckCircle className="w-3 h-3" /> Verified
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Step 2: Address */}
                            {step === 2 && (
                                <div className="space-y-5 animate-in slide-in-from-right-4 fade-in duration-300">
                                    <div className="space-y-1.5">
                                        <Label className="text-gray-700">Street Address</Label>
                                        <div className="relative">
                                            <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                                            <Input
                                                value={formData.address.street}
                                                onChange={(e) => handleAddressChange('street', e.target.value)}
                                                placeholder="123 Oil Field Rd"
                                                className="pl-10 h-11 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                                                autoFocus
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <Label className="text-gray-700">City</Label>
                                            <Input
                                                value={formData.address.city}
                                                onChange={(e) => handleAddressChange('city', e.target.value)}
                                                placeholder="Midland"
                                                className="h-11 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label className="text-gray-700">State</Label>
                                            <Input
                                                value={formData.address.state}
                                                onChange={(e) => handleAddressChange('state', e.target.value)}
                                                placeholder="TX"
                                                className="h-11 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-gray-700">ZIP Code</Label>
                                        <Input
                                            value={formData.address.zip}
                                            onChange={(e) => handleAddressChange('zip', e.target.value)}
                                            placeholder="79701"
                                            className="h-11 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Security */}
                            {step === 3 && (
                                <div className="space-y-5 animate-in slide-in-from-right-4 fade-in duration-300">
                                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 flex gap-3">
                                        <div className="bg-blue-100 p-2 rounded-lg h-fit">
                                            <Lock className="w-5 h-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-blue-900">Set a Password</h4>
                                            <p className="text-sm text-blue-700 mt-1">Use at least 8 characters to keep your account safe.</p>
                                        </div>
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label className="text-gray-700">Password</Label>
                                        <Input
                                            type="password"
                                            value={formData.password}
                                            onChange={(e) => handleChange('password', e.target.value)}
                                            placeholder="••••••••"
                                            className="h-11 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                                            autoFocus
                                        />
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label className="text-gray-700">Confirm Password</Label>
                                        <Input
                                            type="password"
                                            value={formData.confirmPassword}
                                            onChange={(e) => handleChange('confirmPassword', e.target.value)}
                                            placeholder="••••••••"
                                            className="h-11 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer / Navigation */}
                        <div className="bg-gray-50 px-8 py-5 border-t border-gray-100 flex gap-3">
                            {step > 1 && (
                                <Button
                                    variant="outline"
                                    onClick={() => setStep(prev => prev - 1)}
                                    className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-100 hover:text-gray-900 h-12"
                                >
                                    <ChevronLeft className="w-4 h-4 mr-2" /> Back
                                </Button>
                            )}
                            <Button
                                onClick={step === 3 ? handleSubmit : handleNext}
                                disabled={isSubmitting}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white h-12 text-base font-semibold shadow-lg shadow-blue-500/20"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Creating Account...
                                    </>
                                ) : step === 3 ? (
                                    <>
                                        Complete Setup <Check className="w-4 h-4 ml-2" />
                                    </>
                                ) : (
                                    <>
                                        Next Step <ArrowRight className="w-4 h-4 ml-2" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>

                    <p className="text-center mt-6 text-sm text-gray-400">
                        © 2025 OilWellX. Secure Encrypted Connection.
                    </p>
                </div>
            </div>
        </div>
    );
}
